import { useRef, useState, useEffect } from "react";
import { Observer } from "mobx-react";
import { useRouter } from "next/router";
import Head from "next/head";

import { whenAuthenticated } from "components/Authentication";
import MainMenuBar from "components/MainMenuBar/MainMenuBar";
import { Button, Modal } from "components/UI";
import UploadTitle from "components/Upload/Title";
import UploadTemplate from "components/Upload/Template";
import UploadProgress from "components/Upload/Progress";

import translationStore from "stores/translation-store";
import { createUserFromContext, userServerSideProps, useUser } from "utils/user/lib";
import { useApp } from "stores/app/context";
import UploadReady from "components/Upload/Ready";

import { faChevronRight } from "@fortawesome/pro-light-svg-icons/faChevronRight";
import { uploadStateToView, isAndroid, isMobileSafariOrAndroid } from "utils/upload";
import { createTranslationsFromContext } from "utils/translations/lib";

function AddPage()
{
  const router = useRouter();
  const user = useUser();
  const { toast, legacyUpload: upload } = useApp();
  const [view, setView] = useState(uploadStateToView(upload));
  const [showAppModal, setAppModalVisible] = useState(false);
  const onUploadComplete = useRef(() => {});
  const uploadConfig = useRef({
    mode: 'new',
    title: '',
    subTitle: '',
    enrichEnabled: false,
  });

  function startUpload() {
    const {mode} = uploadConfig.current;

    upload.setUser(user);
    upload.setData("mode", mode);
    upload.setUrl(`${process.env.SITE_URL}/upload`);

    upload.onInitUpload(() =>
    {
      return new Promise((resolve, reject) =>
      {
        const {title, subTitle, enrichEnabled} = uploadConfig.current;

        // Construct upload init url with params
        const initUrl = new URL(`${process.env.SITE_URL}/upload/init`);
        if(router.query.f)
          initUrl.searchParams.set("folderkey", router.query.f);
        if(title && title !== "")
          initUrl.searchParams.set("title", title);
        if(subTitle && subTitle !== "")
          initUrl.searchParams.set("subTitle", subTitle);
        if(enrichEnabled)
          initUrl.searchParams.set("autoEnrich", enrichEnabled);

        const [promise] = user.FetchJSON(initUrl.toString());
        promise.then(response =>
        {
          upload.addParam("uploadkey", response.data.uploadKey);

          resolve();
        })
        .catch(reject);
      });
    });

    upload.onComplete(() =>
    {
      return new Promise((resolve, reject) =>
      {
        const uploadkey = upload.getParam("uploadkey");
        const mode = upload.getData("mode");
        const uploadFinishMethod = mode === "new" ? "POST" : "GET";
        const uploadFinishUrl = mode === "new" ? `${process.env.API_URL}/import/upload/${uploadkey}/` : `${process.env.SITE_URL}/upload/finish?uploadkey=${uploadkey}`
        const [promise] = user.FetchJSON(uploadFinishUrl, {
          method: uploadFinishMethod
        });
        promise.then(result =>
        {
          if(result.data.status)
            resolve();
          else
            reject(result.data);
        }).catch(reject);
      });
    });

    upload.onStatus(() => {
      function checkStatus(uploadKey, resolve, reject)
      {
        if (upload.state !== upload.STATE_PROCESSING) {
          return reject();
        }

        const [promise] = user.FetchJSON(`${process.env.API_URL}/import/upload/${uploadKey}/status/`)
        promise.then(result => {
          if(result.data.status) {
            resolve(result.data);
          } else {
            setTimeout(() => {
              checkStatus(uploadKey, resolve);
            }, 1000);
          }
        }).catch(reject);
      }

      function awaitUploadStatus(uploadKey)
      {
        return new Promise((resolve, reject) => {
          checkStatus(uploadKey, resolve, reject);
        });
      }

      return new Promise((resolve, reject) => {
        const uploadkey = upload.getParam("uploadkey");
        awaitUploadStatus(uploadkey).then(result => {
          if(result.status)
          {
            upload.setData("album", result.album);
            upload.setData("errors", result.errors);
            upload.setData("filesCount", result.files);

            resolve();
          }
          else
            reject(result);
        })
        .catch(reject);
      });
    });

    new Promise(resolve =>
    {
      if(user.isLoggedIn)
        return resolve();

      whenAuthenticated(user).then(resolve).catch(e =>
      {
        if(e.error === "popup_closed_by_user")
          upload.clear();
      });
    })
    .then(() =>
    {
      setView('progress');

      upload.start()
      .then(() =>
      {
        onUploadComplete.current();
      })
      .catch((e, file) =>
      {
        const errorProps = new Map();

        if(e)
        {
          if(e.name == "TypeError")
            errorProps.set("", e.toString());

          if(e.type)
            errorProps.set("type", e.type);

          if(file)
            errorProps.set("file", file.name)

          if(e.target)
          {
            if(e.target.readyState)
              errorProps.set("state", e.target.readyState);
            if(e.target.status)
              errorProps.set("status", e.target.status);
            if(e.target.response)
              errorProps.set("response", e.target.response);
          }
        }

        console.log("DEBUG INFO");
        console.log(upload.debug);
        console.log("DEBUG INFO");

        Modal.Alert({
          title: translationStore.translate("base.error"),
          description: `Upload error: ${Array.from(errorProps).map(([key, value]) => `${[key, value].filter(x=>x !== undefined).join(": ")}`).join(", ")}`
        })
        .then(() =>
        {
          upload.cancel();
          upload.clear();

          setView("title");
        });
      });
    });
  }

  function onTitleAndFiles({title, subTitle, files, oldEditorEnabled})
  {
    const mode = oldEditorEnabled ? "classic" : "new";
    uploadConfig.current.mode = mode;
    uploadConfig.current.title = title;
    uploadConfig.current.subTitle = subTitle;

    if(mode==='classic')
    {
      // classic (old editor) gaat gelijk uploaden
      upload.addFiles(files);
      startUpload();
    }else
    {
      // template en files kiezen we in de volgende stap
      setView("template");
    }
  }

  function onTemplate({template, files})
  {
    uploadConfig.current.enrichEnabled = template==='enriched';
    upload.addFiles(files);

    startUpload();
  }

  function onCancel()
  {
    upload.pause();
    Modal.Confirm({
      title: translationStore.translate("myalbums.add.cancel.title"),
      description: translationStore.translate("myalbums.add.cancel.description"),
      okButtonText: translationStore.translate("myalbums.add.cancel.button-ok"),
      cancelButtonText: translationStore.translate("myalbums.add.cancel.button-cancel"),
    })
    .then(() => {
      onUploadCancel();
    })
    .catch(() => {
      upload.resume();
    })
  }

  function onUploadCancel() {
    upload.cancel();
    upload.clear();
    setView('title');
  }

  function onRetryUpload()
  {
    upload.clear();
    setView('title');
  }

  function installApp()
  {
    if(isAndroid())
      window.location.href = "https://play.google.com/store/apps/details?id=com.myalbum.appv4";
    else
      window.location.href = "https://itunes.apple.com/app/id922005321";
  }

  function openApp()
  {
    if(isAndroid())
      window.location.href = "com.myalbum.appv4://";
    else
      window.location.href = "nl.mijnalbum.mijnalbum://";
  }

  // onUploadComplete, via mount
  useEffect(() =>
  {
    // On mount create an onUploadComplete callback function
    onUploadComplete.current = (forceRedirect) => {
      if(forceRedirect || upload.state === upload.STATE_COMPLETE)
      {
        const mode = upload.getData("mode");
        const albumKey = upload.getData("album");
        const files = upload.getData("filesCount");

        if(forceRedirect || files.acceptedCount === files.successCount) {
          if(mode === "new") {
            router.replace(`${process.env.NEXTSITE_URL}/album/${albumKey}`)
            .then(() => {
              upload.clear();
            });
          }
          else
            window.location.href = `${process.env.NEXTSITE_URL}/album/${albumKey}?redirect=1&coverFocus=1`;
        }
        else
          setView(uploadStateToView(upload));
      }
    }

    // When we have unmounted this component show a toast when the upload completes
    return () =>
    {
      onUploadComplete.current = () => {
        const files = upload.getData("filesCount");
        const uploadAllSuccess = files.acceptedCount === files.successCount;
        const uploadSomeSuccess = files.successCount > 0 && files.successCount <= files.acceptedCount;
        const uploadhasErrors = files.failedCount > 0;

        const buttons = [
          {
            icon: faChevronRight,
            onClick: (close) => {
              if(uploadhasErrors)
              {
                close();
                router.push("/myalbums/add");
              }else
              {
                close();
                upload.clear();

                const mode = upload.getData("mode");
                const albumKey = upload.getData("album");

                if(uploadAllSuccess) {
                  if(mode === "new")
                    window.location.href = `${process.env.SITE_URL}/album/${albumKey}`;
                  else
                    window.location.href = `${process.env.SITE_URL}/album/${albumKey}?redirect=1&coverFocus=1`;
                }
                else
                  router.push("/myalbums/add");
              }
            }
          }
        ];

        toast.add({
          message: uploadSomeSuccess ? translationStore.translate("myalbum.add.complete.title-success") : translationStore.translate("myalbum.add.complete.title-failed"),
          autoHide: false,
          closeButton: false,
          buttons
        })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() =>
  {
    if(isMobileSafariOrAndroid())
      setAppModalVisible(true);
  }, [])

  return (
    <Observer>
    {
      () => {
        return (
          <>
            <Head>
              <link rel="preload" href={`${process.env.ASSET_URL}/_static/upload.jpg`} as="image" />
              <title>{translationStore.translate("myalbums.add.title")}</title>
            </Head>

            <MainMenuBar />

            <div className="content">
              <div className="add-modal">
                {view==='title' && <UploadTitle onComplete={onTitleAndFiles} />}
                {view==='template' && <UploadTemplate onComplete={onTemplate} />}
                {view==='progress' && <UploadProgress onCancel={onCancel} />}
                {view==='ready' && <UploadReady onComplete={onUploadComplete} onRetry={onRetryUpload} />}
              </div>

              {
                showAppModal &&
                <>
                  <Modal
                    onClose={() => setAppModalVisible(false)}
                  >
                    <div key="content">
                      <div className="app-modal">
                        <div className="myalbum-logo"></div>
                        <div className="title">{translationStore.translate("myalbum.add.app.title")}</div>
                        <div className="description">{translationStore.translate("myalbum.add.app.description")}</div>
                        <div className="buttons">
                          <Button onClick={() => installApp()} className="filled">{translationStore.translate("myalbum.add.app.install-button")}</Button>
                          <Button onClick={() => openApp()}>{translationStore.translate("myalbum.add.app.open-button")}</Button>
                        </div>
                      </div>
                    </div>
                  </Modal>
                </>
              }

              <style jsx>
              {`
                @media screen and (min-width: 415px) {
                  :global(body) {
                    background-color: white;
                  }
                }

                .app-modal {
                  display: flex;
                  flex: 1;
                  flex-direction: column;
                  align-items: center;
                }

                .app-modal .myalbum-logo {
                  display: flex;
                  width: 76px;
                  height: 76px;
                  justify-content: center;
                  align-items: center;
                  margin-bottom: 30px;
                  border-radius: 15px;
                  margin-top: 5px;
                  box-shadow: 1px 1px 5px 0px rgb(0 0 0 / 20%);
                }

                .app-modal .myalbum-logo:after {
                  content: '';
                  display: block;
                  height: 40px;
                  width: 40px;
                  color: black;
                  mask: url(${process.env.ASSET_URL}/_static/logo.svg) no-repeat center left;
                  background-color: rgba(var(--textColor), 1);

                }
                .app-modal .title {
                  color: inherit;
                  font-family: "Source Sans Pro";
                  font-size: 20px;
                  text-align: center;
                }
                .app-modal .description {
                  color: inherit;
                  font-family: "Source Sans Pro";
                  font-size: 14px;
                  text-align: center;
                }
                .app-modal .buttons {
                  display: flex;
                  flex: 1;
                  flex-direction: row;
                  margin-top: 30px;
                }
                .content {
                  display: flex;
                  flex: 1;
                  flex-direction: column;
                  align-items: center;
                  justify-content: flex-start;
                  padding-top: 65px;
                  box-sizing: border-box;
                  flex-grow: 1 !important;
                  background: transparent url(${process.env.ASSET_URL}/_static/upload.jpg) no-repeat center 65px;
                  background-size: cover;
                  user-select: none;
                  cursor: default;
                }
                @media screen and (max-width: 414px) {
                  .content {
                    background: none;
                    justify-content: center;
                  }
                }
                .content .add-modal {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  padding: 5rem 5rem;
                  background: rgba(var(--backgroundColor), 1);
                  border: 1px solid rgba(var(--border1), 1);
                  border-top: 0;
                  border-radius: 0 0 5px 5px;
                  margin-top: -1px;
                  box-sizing: border-box;
                  overflow: hidden;
                  max-width: 500px;
                }
                @media screen and (max-width: 414px) {
                  .content .add-modal {
                    border: 0px;
                    width: 100%;
                  }
                }
              `}
              </style>
            </div>
          </>
        )
      }
    }
    </Observer>
  );
}

export default AddPage;

export async function getServerSideProps(context) {
  const user = await createUserFromContext(context);
  const translations = await createTranslationsFromContext(context, user.account?.locale.language);

  return {
    props: {
      user: user.JSON,
      translations: translations.JSON
    }
  }
}